import React, { useState } from 'react';
import { ChevronRight, ChevronDown, Target, Brain, Gem, Clock, BarChart3, User } from 'lucide-react';
import { Goal } from '../types';
import { GoalDetailModal } from './Goals/GoalDetailModal';

interface GoalTreeProps {
  goals: Goal[];
  onGoalClick: (goalId: string) => void;
  onGoalUpdated?: () => void;
}

const GoalTree: React.FC<GoalTreeProps> = ({ goals, onGoalClick, onGoalUpdated }) => {
  return (
    <div className="space-y-2">
      {goals
        .filter(goal => !goal.parentGoalId)
        .map(goal => (
          <GoalNode 
            key={goal.id} 
            goal={goal} 
            allGoals={goals}
            level={0}
            onGoalClick={onGoalClick}
            onGoalUpdated={onGoalUpdated}
          />
        ))}
    </div>
  );
};

interface GoalNodeProps {
  goal: Goal;
  allGoals: Goal[];
  level: number;
  onGoalClick: (goalId: string) => void;
  onGoalUpdated?: () => void;
}

const GoalNode: React.FC<GoalNodeProps> = ({ goal, allGoals, level, onGoalClick, onGoalUpdated }) => {
  const [expanded, setExpanded] = useState(level < 2);
  const [showDetailModal, setShowDetailModal] = useState(false);
  
  // Use children from API if available, otherwise filter from allGoals
  const children = goal.children || allGoals.filter(g => g.parentGoalId === goal.id);
  const hasChildren = children.length > 0;

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'completed': return 'text-green-600 bg-green-50';
      case 'in_progress': return 'text-blue-600 bg-blue-50';
      case 'blocked': return 'text-red-600 bg-red-50';
      case 'not_started': return 'text-gray-600 bg-gray-50';
      default: return 'text-gray-600 bg-gray-50';
    }
  };

  const getComplexityColor = (complexity?: number) => {
    if (!complexity) return 'text-gray-500';
    if (complexity <= 3) return 'text-green-600';
    if (complexity <= 6) return 'text-yellow-600';
    return 'text-red-600';
  };

  const formatStatus = (status: string) => {
    return status.replace('_', ' ').replace(/\b\w/g, l => l.toUpperCase());
  };

  return (
    <div className={`${level > 0 ? 'ml-6 pl-4 border-l-2 border-gray-200' : ''}`}>
      <div 
        className={`flex items-center p-3 hover:bg-gray-50 rounded-lg cursor-pointer group border border-transparent hover:border-gray-200 transition-all ${
          level > 0 ? 'bg-gray-25' : ''
        }`}
        onClick={() => setShowDetailModal(true)}
      >
        {hasChildren && (
          <button
            onClick={(e) => {
              e.stopPropagation();
              setExpanded(!expanded);
            }}
            className="mr-2 p-1 hover:bg-gray-200 rounded"
          >
            {expanded ? 
              <ChevronDown className="w-4 h-4 text-gray-500" /> : 
              <ChevronRight className="w-4 h-4 text-gray-500" />
            }
          </button>
        )}
        
        {!hasChildren && level > 0 && (
          <div className="w-6 mr-2 flex justify-center">
            <div className="w-2 h-2 bg-gray-300 rounded-full"></div>
          </div>
        )}
        
        {!hasChildren && level === 0 && (
          <div className="w-6 mr-2" />
        )}
        
        <Target className="w-4 h-4 mr-3 text-gray-400 flex-shrink-0" />
        
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 mb-1">
            <h4 className="font-medium text-gray-900 truncate">
              {goal.name}
            </h4>
            
            <div className="flex items-center gap-2 flex-shrink-0">
              {goal.expertProfile && (
                <div className="w-4 h-4 text-indigo-500" title="Expert Profile configured">
                  <User className="w-4 h-4" />
                </div>
              )}
              {goal.aiGenerated && (
                <div className="w-4 h-4 text-purple-500" title="AI Generated">
                  <Brain className="w-4 h-4" />
                </div>
              )}
              {goal.standalonePotential && (
                <div className="w-4 h-4 text-amber-500" title="Standalone Potential">
                  <Gem className="w-4 h-4" />
                </div>
              )}
            </div>
          </div>
          
          {goal.description && (
            <p className="text-sm text-gray-600 mb-2 line-clamp-2">
              {goal.description}
            </p>
          )}
          
          <div className="flex items-center gap-4 text-xs text-gray-500">
            <span className={`px-2 py-1 rounded-full font-medium ${getStatusColor(goal.status)}`}>
              {formatStatus(goal.status)}
            </span>
            
            {goal.progress > 0 && (
              <div className="flex items-center gap-2">
                <div className="w-16 h-1.5 bg-gray-200 rounded-full overflow-hidden">
                  <div 
                    className="h-full bg-blue-500 transition-all duration-300"
                    style={{ width: `${Math.min(goal.progress, 100)}%` }}
                  />
                </div>
                <span>{Math.round(goal.progress)}%</span>
              </div>
            )}
            
            {goal.complexityScore && (
              <div className="flex items-center gap-1">
                <BarChart3 className="w-3 h-3" />
                <span className={getComplexityColor(goal.complexityScore)}>
                  {goal.complexityScore}/10
                </span>
              </div>
            )}
            
            {goal.estimatedHours && (
              <div className="flex items-center gap-1">
                <Clock className="w-3 h-3" />
                <span>{goal.estimatedHours}h</span>
              </div>
            )}
            
            {goal.targetDate && (
              <div className="flex items-center gap-1">
                <span>Due: {new Date(goal.targetDate).toLocaleDateString()}</span>
              </div>
            )}
          </div>
          
          {goal.aiReasoning && (
            <div className="mt-2 p-2 bg-purple-50 rounded text-xs text-purple-700">
              <strong>AI Insight:</strong> {goal.aiReasoning}
            </div>
          )}
        </div>
      </div>

      {expanded && hasChildren && (
        <div className="mt-1">
          {children
            .sort((a, b) => (a.sortOrder || 0) - (b.sortOrder || 0))
            .map(child => (
              <GoalNode 
                key={child.id}
                goal={child}
                allGoals={allGoals}
                level={level + 1}
                onGoalClick={onGoalClick}
                onGoalUpdated={onGoalUpdated}
              />
            ))}
        </div>
      )}

      {/* Goal Detail Modal */}
      {showDetailModal && (
        <GoalDetailModal
          goal={goal}
          onClose={() => setShowDetailModal(false)}
          onGoalUpdated={(updatedGoal) => {
            setShowDetailModal(false);
            onGoalUpdated?.();
          }}
        />
      )}
    </div>
  );
};

export default GoalTree;